import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import RepairRequestSideMsg from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestSideMsg';
import { buildRepairRequestColumns } from '#/modules/crmMgmt/aftersales/repairRequest/data/repairRequestColumns';
import {
  getRepairRequestApiErrorMessage,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/apiError';
import {
  applyRepairRequestDateRangeToFilters,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairRequestDateListFilter';
import useRepairRequestList from '#/modules/crmMgmt/aftersales/repairRequest/hooks/useRepairRequestList';
import {
  hydrateCustomerFilterLabels,
} from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const REPAIR_REQUEST_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  repairRequestNo: '報修單號',
  repairRequestDate: '報修日',
  status: '狀態',
};

const RepairRequestListPage = () => {
  const canCreate = useCan(PERM.REPAIR_REQUEST_CREATE);
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useRepairRequestList();

  useEffect(() => {
    let cancelled = false;

    hydrateCustomerFilterLabels(listQuery.filters?.customerId, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.customerId,
    customerLabelCache,
  ]);

  // 起迄須同一次寫入 URL；連續兩次 setFilter 會以舊 listQuery 為底，後者蓋掉前者
  const setRepairRequestDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyRepairRequestDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'repairRequestDateFrom' && value === undefined) {
      setRepairRequestDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setRepairRequestDateRangeFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildRepairRequestColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onRepairRequestDateRangeChange: setRepairRequestDateRangeFilter,
        customerLabelCache,
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setRepairRequestDateRangeFilter,
      customerLabelCache,
      labelCacheVersion,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  useEffect(() => {
    if (!displayData?.id) {
      return;
    }
    const fresh = rows.find((row) => row.id === displayData.id);
    if (!fresh) {
      return;
    }
    const sameStatus = fresh.status === displayData.status;
    const sameUpdatedAt = fresh.record?.updatedAt === displayData.record?.updatedAt;
    if (sameStatus && sameUpdatedAt) {
      return;
    }
    setSelectedData(fresh);
  }, [rows, displayData, setSelectedData]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate
    ? <ListAddButton to={ROUTES.repairRequestCreate} />
    : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <AftersalesOpsPageHeader page="repairRequest" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getRepairRequestApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋報修單號或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={REPAIR_REQUEST_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>
          <RepairRequestSideMsg
            displayData={displayData}
            onRefetch={refetch}
            onDeleted={() => setSelectedData(null)}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default RepairRequestListPage;
